import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import type { ImageAttachment, MentionRef, ViewContext } from '@localagent/sdk';

const MAX_QUEUE_SIZE = 20;

export interface QueuedMessage {
  id: string;
  content: string;
  createdAt: string;
  mentions?: MentionRef[];
  /** Stamped when the user pressed send, never re-derived at drain (app-shell ADR-015). */
  viewContext?: ViewContext;
  /** Already-uploaded image handles, captured at queue time like viewContext. */
  attachments?: ImageAttachment[];
}

const messageQueueAtomFamily = atomFamily((_sessionId: string) =>
  atom<QueuedMessage[]>([]),
);

export function useMessageQueue(sessionId: string | undefined) {
  const [queue, setQueue] = useAtom(messageQueueAtomFamily(sessionId ?? ''));

  const addMessage = useCallback((
    content: string,
    mentions?: MentionRef[],
    viewContext?: ViewContext,
    attachments?: ImageAttachment[],
  ) => {
    setQueue(prev => {
      if (prev.length >= MAX_QUEUE_SIZE) return prev;
      return [...prev, {
        id: crypto.randomUUID(),
        content,
        createdAt: new Date().toISOString(),
        ...(mentions && mentions.length > 0 ? { mentions } : {}),
        ...(viewContext ? { viewContext } : {}),
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
      }];
    });
  }, [setQueue]);

  const removeMessage = useCallback((id: string) => {
    setQueue(prev => prev.filter(m => m.id !== id));
  }, [setQueue]);

  const reorder = useCallback((fromIndex: number, toIndex: number) => {
    setQueue(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, [setQueue]);

  const clear = useCallback(() => setQueue([]), [setQueue]);

  return {
    queue,
    queueCount: queue.length,
    addMessage,
    removeMessage,
    reorder,
    clear,
  };
}
